import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateFunctionTagsApplicationErrors,
  BulkUpdateFunctionTagsByFilterApplicationErrors,
  BulkUpdateFunctionTagsByFilterOptions,
  BulkUpdateFunctionTagsByFilterResponse,
  BulkUpdateFunctionTagsOptions,
  BulkUpdateFunctionTagsResponse,
  FunctionCreatedEnvelope,
  FunctionDeletedEnvelope,
  FunctionTagsModifiedEnvelope,
  FunctionUpdatedEnvelope,
  FunctionsQueryBuilder,
  QueryFunctionsResponse,
  UpdateFunction,
  _Function,
  _FunctionQuery,
  bulkUpdateFunctionTags as universalBulkUpdateFunctionTags,
  bulkUpdateFunctionTagsByFilter as universalBulkUpdateFunctionTagsByFilter,
  createFunction as universalCreateFunction,
  deleteFunction as universalDeleteFunction,
  getFunction as universalGetFunction,
  queryFunctions as universalQueryFunctions,
  typedQueryFunctions as universalTypedQueryFunctions,
  updateFunction as universalUpdateFunction,
} from './functions-v1-function-functions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/functions' };

export function createFunction(
  httpClient: HttpClient
): CreateFunctionSignature {
  return (_function?: _Function) =>
    universalCreateFunction(
      _function,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateFunctionSignature {
  /**
   * Creates a function with the specified configuration. After creation, you'll need to attach automation logic using the Function Methods API and configure any required service plugin settings before the function can be activated.
   *
   * This isn't the recommended way to create a function. Learn more about [function creation](https://dev.wix.com/docs/api-reference/business-management/functions/about-function-creation-and-activation#quick-creation-and-configuration).
   * @param - Function to create.
   * @returns The created function.
   */
  (_function?: _Function): Promise<
    NonNullablePaths<
      _Function,
      `activationStatus` | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function getFunction(httpClient: HttpClient): GetFunctionSignature {
  return (functionId: string) =>
    universalGetFunction(
      functionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetFunctionSignature {
  /**
   * Retrieves a function.
   * @param - ID of the function to retrieve.
   * @returns The retrieved function.
   */
  (functionId: string): Promise<
    NonNullablePaths<
      _Function,
      `activationStatus` | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function updateFunction(
  httpClient: HttpClient
): UpdateFunctionSignature {
  return (_id: string, _function?: UpdateFunction) =>
    universalUpdateFunction(
      _id,
      _function,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateFunctionSignature {
  /**
   * Updates a function.
   *
   * Each time the function is updated,
   * `revision` increments by 1.
   * The existing `revision` must be passed when updating the function.
   * This ensures you're working with the latest version of the function
   * and prevents unintended overwrites.
   * @param - Function ID.
   * @returns Updated function.
   */
  (_id: string, _function?: UpdateFunction): Promise<
    NonNullablePaths<
      _Function,
      `activationStatus` | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteFunction(
  httpClient: HttpClient
): DeleteFunctionSignature {
  return (functionId: string) =>
    universalDeleteFunction(
      functionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteFunctionSignature {
  /**
   * Deletes a function.
   *
   * Deleting a function permanently removes it from the Function List
   * in the site's dashboard.
   * @param - ID of the function to delete.
   */
  (functionId: string): Promise<void>;
}

export function queryFunctions(
  httpClient: HttpClient
): QueryFunctionsSignature {
  return () =>
    universalQueryFunctions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryFunctionsSignature {
  /**
   * Creates a query to retrieve a list of functions.
   *
   * The Query Functions method builds a query to retrieve a list of functions and returns a `FunctionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `FunctionsQueryBuilder` methods onto the query. `FunctionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Functions returns.
   *
   * Query Functions has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `FunctionsQueryResult`.
   */
  (): FunctionsQueryBuilder;
}

export function typedQueryFunctions(
  httpClient: HttpClient
): TypedQueryFunctionsSignature {
  return (query: _FunctionQuery) =>
    universalTypedQueryFunctions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryFunctionsSignature {
  /** */
  (query: _FunctionQuery): Promise<
    NonNullablePaths<
      QueryFunctionsResponse,
      `functions` | `functions.${number}.activationStatus`,
      4
    >
  >;
}

export function bulkUpdateFunctionTags(
  httpClient: HttpClient
): BulkUpdateFunctionTagsSignature {
  return (
    ids: string[],
    options?: NonNullablePaths<BulkUpdateFunctionTagsOptions, `assignTags`, 2>
  ) =>
    universalBulkUpdateFunctionTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateFunctionTagsSignature {
  /**
   * Synchronously update tags on multiple functions.
   * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
   * @param - IDs of functions to update tags for.
   */
  (
    ids: string[],
    options?: NonNullablePaths<BulkUpdateFunctionTagsOptions, `assignTags`, 2>
  ): Promise<
    NonNullablePaths<
      BulkUpdateFunctionTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateFunctionTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateFunctionTagsByFilter(
  httpClient: HttpClient
): BulkUpdateFunctionTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateFunctionTagsByFilterOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdateFunctionTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateFunctionTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple functions.
   * An empty filter updates all functions.
   * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
   * @param - Filter.
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateFunctionTagsByFilterOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<BulkUpdateFunctionTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateFunctionTagsByFilterApplicationErrors;
    }
  >;
}

export const onFunctionCreated = /*#__PURE__*/ EventDefinition(
  'wix.functions.v1.function_created',
  true,
  (event: FunctionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<FunctionCreatedEnvelope>();
export const onFunctionDeleted = /*#__PURE__*/ EventDefinition(
  'wix.functions.v1.function_deleted',
  true,
  (event: FunctionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<FunctionDeletedEnvelope>();
export const onFunctionTagsModified = /*#__PURE__*/ EventDefinition(
  'wix.functions.v1.function_tags_modified',
  true,
  (event: FunctionTagsModifiedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.function.createdDate' },
            { path: 'data.function.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<FunctionTagsModifiedEnvelope>();
export const onFunctionUpdated = /*#__PURE__*/ EventDefinition(
  'wix.functions.v1.function_updated',
  true,
  (event: FunctionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<FunctionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActivationStatus,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkUpdateFunctionTagsByFilterOptions,
  BulkUpdateFunctionTagsByFilterRequest,
  BulkUpdateFunctionTagsByFilterResponse,
  BulkUpdateFunctionTagsOptions,
  BulkUpdateFunctionTagsRequest,
  BulkUpdateFunctionTagsResponse,
  BulkUpdateFunctionTagsResult,
  CreateFunctionRequest,
  CreateFunctionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteFunctionRequest,
  DeleteFunctionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftDiscarded,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FieldAndCount,
  FunctionCreatedEnvelope,
  FunctionDeletedEnvelope,
  FunctionTagsModifiedEnvelope,
  FunctionUpdatedEnvelope,
  FunctionsQueryBuilder,
  FunctionsQueryResult,
  GetFunctionRequest,
  GetFunctionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListValuesByFieldRequest,
  ListValuesByFieldResponse,
  MessageEnvelope,
  QueryFunctionsRequest,
  QueryFunctionsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  TagsModified,
  UpdateFunction,
  UpdateFunctionExternalIdsRequest,
  UpdateFunctionExternalIdsResponse,
  UpdateFunctionRequest,
  UpdateFunctionResponse,
  WebhookIdentityType,
  _Function,
  _FunctionQuerySpec,
  utils,
} from './functions-v1-function-functions.universal.js';
